import React from 'react'
import PropTypes from 'prop-types'
import Portal from 'react-portal'
import classNames from 'classnames'

import { sendHoverSoundCommand, sendCancelClickSoundCommand } from '../../helpers/web_to_client'

import styles from './Popover.css'

const ESC_KEY_CODE = 27

const WINDOW_BOUNDS_OFFSET = 25

const ARROW_HALF_VISIBLE_WIDTH = 60
const ARROW_HALF_VISIBLE_HEIGHT = 60

const SHOW_DELAY = 100

const POSITIONS = [
    'top',
    'right',
    'bottom',
    'left',
]

const ALIGNMENTS = [
    'start',
    'middle',
    'end',
]

const propTypes = {
    /**
     * Content of the popover.
     */
    children: PropTypes.node.isRequired,

    /**
     * Position relative to the target, determines side where popover will be shown.
     */
    position: PropTypes.oneOf(POSITIONS).isRequired,

    /**
     * Alignment relative to the target, determines arrow alignment inside popover and popovers orientation at the side it was shown.
     */
    alignment: PropTypes.oneOf(ALIGNMENTS).isRequired,

    /**
     * Value of popovers offset to the target, both for horizontal and vertical orientations
     */
    targetOffset: PropTypes.number,

    /**
     * Bounds of viewport
     */
    bounds: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        bottom: PropTypes.number,
        left: PropTypes.number,
    }),

    /**
     * Width of the popover, can override max-width
     */
    width: PropTypes.number,

    /**
     * Display popover fixed at the center of the screen. Position and alignment properties will be ignored.
     */
    isCentered: PropTypes.bool,

    mixClassName: PropTypes.string,

    /**
     * Callback to be called before popover was shown
     */
    beforeShow: PropTypes.func,
    /**
     * Callback to be called before popover was hidden
     */
    beforeHide: PropTypes.func,

    /**
     * Callback to be called after popover was shown
     */
    afterShow: PropTypes.func,

    /**
     * Callback to be called after popover was hidden
     */
    afterHide: PropTypes.func,

    /**
     * This prop used only for demonstration needs, so it is not needed in documentation
     *
     * @ignore
     */
    isDemo: PropTypes.bool,
    isLocal: PropTypes.bool,
}

const defaultProps = {
    position: 'bottom',
    alignment: 'middle',
    targetOffset: 0,
}

class Popover extends React.Component {

    constructor(props) {
        super(props)

        this.state = {
            opened: this.props.isDemo,
            position: this.props.position,
            alignment: this.props.alignment,
        }

        this.target = this.props.isDemo ? <div /> : null
        this.handleKeyDown = this.handleKeyDown.bind(this)
        this.hide = this.hide.bind(this)

        this.setPopoverRef = c => this._popover = c
        this.setArrowRef = c => this._arrow = c
    }

    componentWillReceiveProps(nextProps) {
        if (
            nextProps.position !== this.props.position ||
            nextProps.alignment !== this.props.alignment
        ) {
            this.setState({
                alignment: nextProps.alignment,
                position: nextProps.position
            })
        }
    }

    componentWillMount() {
        window.addEventListener('click', this.hide)
        window.addEventListener('keydown', this.handleKeyDown, true)
        window.addEventListener('resize', this.hide)
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.hide)
        window.removeEventListener('keydown', this.handleKeyDown)
        window.removeEventListener('resize', this.hide)
    }

    componentDidUpdate() {
        if (!this.state.opened) return

        if (!this.props.isCentered) {
            this.setPosition()
        }
    }

    clearTimeout() {
        this.timeoutId && clearTimeout(this.timeoutId)
    }

    // Display control
    show(target) {
        this.target = target

        if (this.props.beforeShow) this.props.beforeShow()

        const show = () => {
            this.setState({ opened: true }, () => {
                if (this.props.afterShow) this.props.afterShow()
            })
        }

        this.timeoutId = setTimeout(show, SHOW_DELAY)
    }

    hide() {
        this.clearTimeout()
        if (!this.state.opened) return

        if (this.props.beforeHide) this.props.beforeHide()

        this.setState({
            position: this.props.position,
            opened: false,
        }, () => {
            if (this.props.afterHide) this.props.afterHide()
        })
    }

    toggle(target) {
        if (this.state.opened) {
            this.hide()
        } else {
            this.show(target)
        }
    }

    // Handlers
    handleClick(e) {
        e.stopPropagation()
    }

    handleKeyDown(e) {
        if (e.keyCode === ESC_KEY_CODE) {
            if (this.state.opened) {
                e.stopPropagation()
            }

            this.hide()
        }
    }

    // Positioning methods
    arrowHorizontalAlignment(value, popover, target, isLocal = false) {
        let arrowLeft = value

        const originLeft = isLocal ? 0 : target.left

        switch (this.state.alignment) {
            case 'start':
                arrowLeft = originLeft - popover.left + target.width / 2
                break
            case 'middle':
                arrowLeft = this._popover.clientWidth / 2
                break
            case 'end':
                arrowLeft = originLeft - popover.left + target.width / 2
                break
        }

        return arrowLeft
    }

    arrowVerticalAlignment(value, popover, target, isLocal = false) {
        let arrowTop = value

        const originTop = isLocal ? 0 : target.top

        switch (this.state.alignment) {
            case 'start':
                arrowTop = originTop - popover.top + target.height / 2
                break
            case 'middle':
                arrowTop = this._popover.clientHeight / 2
                break
            case 'end':
                arrowTop = originTop - popover.top + target.height / 2
                break
        }

        return arrowTop
    }

    arrowAlignment(topValue, leftValue, popover, target, isLocal) {
        let arrowTop = topValue
        let arrowLeft = leftValue

        const { position } = this.state

        const verticalEdges = position === 'right' || position === 'left'
        const horizontalEdges = position === 'top' || position === 'bottom'

        if (verticalEdges) {
            arrowTop = this.arrowVerticalAlignment(arrowTop, popover, target, isLocal)
        }

        if (horizontalEdges) {
            arrowLeft = this.arrowHorizontalAlignment(arrowLeft, popover, target, isLocal)
        }

        return {
            arrowTop,
            arrowLeft,
        }
    }

    arrowPosition(popover, target, isLocal = false) {
        let arrowTop
        let arrowLeft

        switch (this.state.position) {
            case 'top':
                arrowTop = this._popover.clientHeight
                arrowLeft = this._popover.clientWidth / 2
                break
            case 'right':
                arrowTop = this._popover.clientHeight / 2
                arrowLeft = 0
                break
            case 'bottom':
                arrowTop = 0
                arrowLeft = this._popover.clientWidth / 2
                break
            case 'left':
                arrowTop = this._popover.clientHeight / 2
                arrowLeft = this._popover.clientWidth
                break
        }

        const arrowPosition = {
            arrowTop,
            arrowLeft,
            ...this.arrowAlignment(arrowTop, arrowLeft, popover, target, isLocal),
        }

        return arrowPosition
    }

    popoverHorizontalAlignment(value, target, isLocal = false) {
        let popoverLeft = value

        const originLeft = isLocal ? 0 : target.left

        switch (this.state.alignment) {
            case 'start':
                popoverLeft = originLeft - this.props.targetOffset
                break
            case 'middle':
                popoverLeft = originLeft - this._popover.clientWidth / 2 + target.width / 2
                break
            case 'end':
                popoverLeft = originLeft - this._popover.clientWidth + target.width + this.props.targetOffset
                break
        }

        if (target.width < ARROW_HALF_VISIBLE_WIDTH) {
            switch (this.state.alignment) {
                case 'start':
                    popoverLeft = originLeft + target.width / 2 - ARROW_HALF_VISIBLE_WIDTH
                    break
                case 'middle':
                    break
                case 'end':
                    popoverLeft = originLeft + target.width / 2 - this._popover.clientWidth + ARROW_HALF_VISIBLE_WIDTH
                    break
            }
        }

        return popoverLeft
    }

    popoverVerticalAlignment(value, target, isLocal = false) {
        let popoverTop = value

        const originTop = isLocal ? 0 : target.top

        switch (this.state.alignment) {
            case 'start':
                popoverTop = originTop - this.props.targetOffset
                break
            case 'middle':
                popoverTop = originTop - this._popover.clientHeight / 2 + target.height / 2
                break
            case 'end':
                popoverTop = originTop - this._popover.clientHeight + target.height + this.props.targetOffset
                break
        }

        if (target.height < ARROW_HALF_VISIBLE_HEIGHT) {
            switch (this.state.alignment) {
                case 'start':
                    popoverTop = originTop + target.height / 2 - ARROW_HALF_VISIBLE_HEIGHT
                    break
                case 'middle':
                    break
                case 'end':
                    popoverTop = originTop + target.height / 2 - this._popover.clientHeight + ARROW_HALF_VISIBLE_HEIGHT
                    break
            }
        }

        return popoverTop
    }

    popoverAlignment(top, left, target, isLocal) {
        let popoverTop = top
        let popoverLeft = left

        const { position } = this.state
        const verticalEdges = position === 'right' || position === 'left'
        const horizontalEdges = position === 'top' || position === 'bottom'

        if (verticalEdges) {
            popoverTop = this.popoverVerticalAlignment(popoverTop, target, isLocal)
        }

        if (horizontalEdges) {
            popoverLeft = this.popoverHorizontalAlignment(popoverLeft, target, isLocal)
        }

        return {
            popoverTop,
            popoverLeft,
        }
    }

    initialPosition(target, isLocal = false) {
        let popoverTop
        let popoverLeft

        const originTop = isLocal ? 0 : target.top
        const originLeft = isLocal ? 0 : target.left

        switch (this.state.position) {
            case 'top':
                popoverTop = originTop - this._popover.clientHeight
                popoverLeft = originLeft - this._popover.clientWidth / 2 + target.width / 2
                break
            case 'right':
                popoverTop = originTop + target.height / 2 - this._popover.clientHeight / 2
                popoverLeft = originLeft + target.width
                break
            case 'bottom':
                popoverTop = originTop + target.height
                popoverLeft = originLeft - this._popover.clientWidth / 2 + target.width / 2
                break
            case 'left':
                popoverTop = originTop + target.height / 2 - this._popover.clientHeight / 2
                popoverLeft = originLeft - this._popover.clientWidth
                break
        }

        const popoverPosition = {
            popoverTop,
            popoverLeft,
            ...this.popoverAlignment(popoverTop, popoverLeft, target, isLocal),
        }

        return popoverPosition
    }

    correctPosition(top, left, aLeft, aTop) {
        let popoverTop = top
        let popoverLeft = left
        let arrowLeft = aLeft
        let arrowTop = aTop

        const target = this.target.getBoundingClientRect()
        const popoverWidth = this._popover.clientWidth
        const popoverHeight = this._popover.clientHeight

        // Custom offset on certain DOM elements
        const popoverOffset = this.target.dataset.popoverOffset
        let offset = {
            top: 0,
            left: 0,
        }
        if (popoverOffset) {
            offset = {
                offset,
                ...JSON.parse(popoverOffset),
            }
        }

        popoverTop = popoverTop + offset.top
        popoverLeft = popoverLeft + offset.left

        const { clientHeight, clientWidth } = document.documentElement

        let bounds = {
            top: WINDOW_BOUNDS_OFFSET,
            right: WINDOW_BOUNDS_OFFSET,
            bottom: WINDOW_BOUNDS_OFFSET,
            left: WINDOW_BOUNDS_OFFSET,
            ...this.props.bounds,
        }

        // Calculate popover position limits
        const topLimit = bounds.top
        const rightLimit = clientWidth - bounds.right
        const bottomLimit = clientHeight - bounds.bottom
        const leftLimit = bounds.left

        const popoverClientLeft = popoverLeft
        const popoverClientTop = popoverTop
        const targetLeft = target.left
        const targetTop = target.top

        const fitsTop = (popoverTop) => popoverTop >= topLimit
        const fitsRight = (popoverLeft) => popoverLeft + popoverWidth <= rightLimit
        const fitsBottom = (popoverTop) => popoverTop + popoverHeight <= bottomLimit
        const fitsLeft = (popoverLeft) => popoverLeft >= leftLimit

        if (!fitsLeft(popoverClientLeft)) {
            popoverLeft = targetLeft - this.props.targetOffset
            arrowLeft = targetLeft - popoverLeft + target.width / 2
        } else if (!fitsRight(popoverClientLeft)) {
            popoverLeft = targetLeft - popoverWidth + target.width + this.props.targetOffset
            arrowLeft = targetLeft - popoverLeft + target.width / 2
        }

        let fittedTop = true
        let fittedBottom = true

        if (!fitsBottom(popoverClientTop)) {
            popoverTop = targetTop - popoverHeight
            arrowTop = popoverHeight

            fittedBottom = false

            if (!fitsTop(popoverTop)) {
                popoverTop = targetTop + target.height
                arrowTop = 0

                fittedTop = false
            }
        }

        if (!fitsTop(popoverClientTop)) {
            popoverTop = targetTop + target.height
            arrowTop = 0

            fittedTop = false

            if (!fitsBottom(popoverTop)) {
                popoverTop = targetTop - popoverHeight
                arrowTop = popoverHeight

                fittedBottom = false
            }
        }

        // Adjust vertical position if Popover not fits in screen
        let calculatedPosition = this.props.position

        if (this.props.position === 'top' && (!fittedTop || !fittedBottom)) {
            calculatedPosition = fittedBottom ? 'bottom' : 'top'
        }

        if (this.props.position === 'bottom' && (!fittedBottom || !fittedTop)) {
            calculatedPosition = fittedTop ? 'top' : 'bottom'
        }

        if (this.props.position === 'right' || this.props.position === 'left') {
            if (!fittedTop || !fittedBottom) {
                calculatedPosition = fittedTop ? 'top' : 'bottom'
            }
        }

        // Set new position
        if (calculatedPosition !== this.state.position && (!fittedTop || !fittedBottom)) {
            this.setState({ position: calculatedPosition })
        } else if (this.props.position !== this.state.position && !fittedBottom && !fittedTop) {
            this.setState({ position: this.props.position })
        }

        return {
            popoverTop,
            popoverLeft,
            arrowLeft,
            arrowTop,
        }
    }

    setPosition() {
        const target = this.target.getBoundingClientRect()

        let windowScrollY = window.scrollY || window.pageYOffset
        let { popoverLeft, popoverTop } = this.initialPosition(target)

        const popoverPosition = {
            top: popoverTop,
            left: popoverLeft,
        }

        let { arrowLeft, arrowTop } = this.arrowPosition(popoverPosition, target)

        const correctedPosition = this.correctPosition(popoverTop, popoverLeft, arrowLeft, arrowTop)

        if (this.props.isLocal) {
            const localPosition = this.initialPosition(target, true)
            const localPopoverPosition = {
                top: localPosition.popoverTop,
                left: localPosition.popoverLeft,
            }
            const localArrowPosition = this.arrowPosition(localPopoverPosition, target, true)

            popoverTop = localPosition.popoverTop + correctedPosition.popoverTop - popoverTop
            popoverLeft = localPosition.popoverLeft + correctedPosition.popoverLeft - popoverLeft
            arrowTop = localArrowPosition.arrowTop + correctedPosition.arrowTop - arrowTop
            arrowLeft = localArrowPosition.arrowLeft + correctedPosition.arrowLeft - arrowLeft
        } else {
            popoverTop = correctedPosition.popoverTop + windowScrollY
            popoverLeft = correctedPosition.popoverLeft
            arrowLeft = correctedPosition.arrowLeft
            arrowTop = correctedPosition.arrowTop
        }

        this.setStyle({
            popoverLeft,
            popoverTop,
            arrowLeft,
            arrowTop,
        })
    }

    setStyle(style) {
        this._arrow.style.left = `${style.arrowLeft}px`
        this._arrow.style.top = `${style.arrowTop}px`

        this._popover.style.left = `${style.popoverLeft}px`
        this._popover.style.top = `${style.popoverTop}px`
    }

    render() {
        const style = this.props.width ? { width: this.props.width } : null

        const classNameBase = classNames(
            styles.base,
            {
                [styles[`base__${this.state.position}`]]: !this.props.isCentered,
                [styles.isCentered]: this.props.isCentered,
                [styles.isOpened]: this.state.opened,
                [styles.isDemo]: this.props.isDemo,
            },
            this.props.mixClassName,
        )

        const popover = (
            <div
                ref={this.setPopoverRef}
                className={classNameBase}
                onClick={this.handleClick}
                style={style}
            >
                <span className={styles.bgGradient} />

                <div className={styles.body}>
                    <span
                        className={styles.close}
                        onClick={this.hide}
                        onMouseDown={sendCancelClickSoundCommand}
                        onMouseEnter={sendHoverSoundCommand}
                    />
                    <div className={styles.content}>
                        {this.props.children}
                    </div>
                </div>

                <span className={styles.arrow} ref={this.setArrowRef} />
            </div>
        )

        return (this.props.isDemo || this.props.isLocal) ? popover : (
            <Portal isOpened={this.state.opened}>{popover}</Portal>
        )
    }
}

Popover.propTypes = propTypes
Popover.defaultProps = defaultProps

Popover.POSITIONS = POSITIONS
Popover.ALIGNMENTS = ALIGNMENTS

export default Popover



// WEBPACK FOOTER //
// ../node_modules/react-mt-bootstrap/lib/components/Popover/Popover.jsx